import { BoundingBox as BJSBoundingBox } from "@babylonjs/core/Culling/boundingBox";
import { Matrix, Vector3 } from "@babylonjs/core/Maths/math.vector";
import { InstancedMesh } from "@babylonjs/core/Meshes/instancedMesh";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexBuffer } from "@babylonjs/core/Buffers/buffer";
import { ERR_INVALID_ARGUMENT } from "../../utils/error";
import { EPSILON } from "../../utils/math";
import { VECTOR3_MAX, VECTOR3_MIN } from "../utils/MathUtils";
import { getLocalMatrixToRef } from "../utils/MeshUtils";
var BoundingBox = /** @class */ (function () {
    function BoundingBox(pMin, pMax) {
        if (pMin != null)
            this.min = pMin.clone();
        else
            this.min = VECTOR3_MAX.clone();
        if (pMax != null)
            this.max = pMax.clone();
        else
            this.max = VECTOR3_MIN.clone();
    }
    BoundingBox.prototype.getCenter = function (pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        var tCenter = Vector3.Center(this.min, this.max);
        if (pWorldMatrix != null)
            Vector3.TransformCoordinatesToRef(tCenter, pWorldMatrix, tCenter);
        return (tCenter);
    };
    BoundingBox.prototype.getSize = function () {
        return (this.max.subtract(this.min));
    };
    BoundingBox.prototype.getRadius = function () {
        return (Vector3.Distance(this.min, this.max) * 0.5);
    };
    /**
     * Returns an array with the 8 box corners in fixed order.
     *
     * ```text
     * Order of points
     * ..6--------5
     * ./ |     / |
     * 1--|----2  |
     * |  7----|--4
     * | /     | /
     * 0-------3
     *
     * 0 = (min.x, min.y, min.z)
     * 1 = (min.x, max.y, min.z)
     * 2 = (max.x, max.y, min.z)
     * 3 = (max.x, min.y, min.z)
     * 4 = (max.x, min.y, max.z)
     * 5 = (max.x, max.y, max.z)
     * 6 = (min.x, max.y, max.z)
     * 7 = (min.x, min.y, max.z)
     * ```
     */
    BoundingBox.prototype.getPoints = function (pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        var tPoints = new Array(8);
        tPoints[0] = this.min.clone();
        tPoints[1] = this.min.clone();
        tPoints[1].y = this.max.y;
        tPoints[2] = this.max.clone();
        tPoints[2].z = this.min.z;
        tPoints[3] = this.min.clone();
        tPoints[3].x = this.max.x;
        tPoints[4] = this.max.clone();
        tPoints[4].y = this.min.y;
        tPoints[5] = this.max.clone();
        tPoints[6] = this.max.clone();
        tPoints[6].x = this.min.x;
        tPoints[7] = this.min.clone();
        tPoints[7].z = this.max.z;
        if (pWorldMatrix != null) {
            for (var i = 0; i < tPoints.length; ++i) {
                Vector3.TransformCoordinatesToRef(tPoints[i], pWorldMatrix, tPoints[i]);
            }
        }
        return (tPoints);
    };
    BoundingBox.prototype.isValid = function () {
        return (BoundingBox.IsMinMaxValid(this.min, this.max));
    };
    BoundingBox.prototype.reset = function () {
        this.min = VECTOR3_MAX.clone();
        this.max = VECTOR3_MIN.clone();
    };
    BoundingBox.prototype.clone = function () {
        return (new BoundingBox(this.min, this.max));
    };
    BoundingBox.prototype.expand = function (pBox) {
        return (this.clone().expandInPlace(pBox));
    };
    BoundingBox.prototype.expandInPlace = function (pBox) {
        if (pBox != null && pBox.isValid()) {
            this.expandByPointInPlace(pBox.min);
            this.expandByPointInPlace(pBox.max);
        }
        return (this);
    };
    BoundingBox.prototype.expandByPoint = function (pPoint) {
        return (this.clone().expandByPointInPlace(pPoint));
    };
    BoundingBox.prototype.expandByPointInPlace = function (pPoint) {
        this.min.minimizeInPlace(pPoint);
        this.max.maximizeInPlace(pPoint);
        return (this);
    };
    BoundingBox.prototype.transform = function (pMatrix) {
        return (this.clone().transformInPlace(pMatrix));
    };
    BoundingBox.prototype.transformInPlace = function (pMatrix) {
        if (pMatrix == null || !this.isValid())
            return (this);
        var tPoints = this.getPoints();
        this.min = VECTOR3_MAX.clone();
        this.max = VECTOR3_MIN.clone();
        for (var i = 0; i < tPoints.length; ++i) {
            this.expandByPointInPlace(Vector3.TransformCoordinates(tPoints[i], pMatrix));
        }
        return (this);
    };
    BoundingBox.prototype.isInFrustum = function (pFrustum, pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        if (!this.isValid() || !this.isBoundingSphereInFrustum(pFrustum, pWorldMatrix))
            return (false);
        var tPoints = this.getPoints(pWorldMatrix);
        return (BJSBoundingBox.IsInFrustum(tPoints, pFrustum));
    };
    BoundingBox.prototype.isCompletelyInFrustum = function (pFrustum, pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        if (!this.isValid() || !this.isBoundingSphereInFrustum(pFrustum, pWorldMatrix))
            return (false);
        var tPoints = this.getPoints(pWorldMatrix);
        return (BJSBoundingBox.IsCompletelyInFrustum(tPoints, pFrustum));
    };
    /**
     * Checks if this bounding box intersects with another bounding box.
     * @param pBounds The other bounding box.
     * @param pBorderIntersections If true, it counts as an intersection if the two bounding boxes are touching eachother.
     * @param pEpsilon Tolerance value for the check (default: utils.math.EPSILON)
     */
    BoundingBox.prototype.intersects = function (pBounds, pBorderIntersections, pEpsilon) {
        if (pEpsilon === void 0) { pEpsilon = EPSILON; }
        if (pBounds == null || !pBounds.isValid())
            return (false);
        return (this.intersectsMinMax(pBounds.min, pBounds.max, pBorderIntersections, pEpsilon));
    };
    /**
     * Checks if this bounding box intersects with the given point.
     * @param pPoint The point.
     * @param pBorderIntersections If true, it counts as an intersection if the point is directly on the border of this bounding box.
     * @param pEpsilon Tolerance value for the check (default: utils.math.EPSILON)
     */
    BoundingBox.prototype.intersectsPoint = function (pPoint, pBorderIntersections, pEpsilon) {
        if (pEpsilon === void 0) { pEpsilon = EPSILON; }
        if (pPoint == null)
            return (false);
        return (this.intersectsMinMax(pPoint, pPoint, pBorderIntersections, pEpsilon));
    };
    /**
     * Checks if this bounding box intersects with another bounding box given as a min and max vector.
     * @param pMin The min vector of the bonding box.
     * @param pMax The max vector of the bonding box.
     * @param pBorderIntersections If true, it counts as an intersection if the two bounding boxes are touching eachother.
     * @param pEpsilon Tolerance value for the check (default: utils.math.EPSILON)
     */
    BoundingBox.prototype.intersectsMinMax = function (pMin, pMax, pBorderIntersections, pEpsilon) {
        if (pEpsilon === void 0) { pEpsilon = EPSILON; }
        if (pMin == null || pMax == null || !this.isValid())
            return (false);
        if (pBorderIntersections) {
            if (pMin.x - this.max.x > pEpsilon || this.min.x - pMax.x > pEpsilon)
                return (false);
            if (pMin.y - this.max.y > pEpsilon || this.min.y - pMax.y > pEpsilon)
                return (false);
            if (pMin.z - this.max.z > pEpsilon || this.min.z - pMax.z > pEpsilon)
                return (false);
        }
        else {
            if (this.max.x - pMin.x <= pEpsilon || pMax.x - this.min.x <= pEpsilon)
                return (false);
            if (this.max.y - pMin.y <= pEpsilon || pMax.y - this.min.y <= pEpsilon)
                return (false);
            if (this.max.z - pMin.z <= pEpsilon || pMax.z - this.min.z <= pEpsilon)
                return (false);
        }
        return (true);
    };
    /**
     * Calculates the bounding box for the given node. If pVisibleOnly is true invisible nodes are ignored.
     */
    BoundingBox.FromNode = function (pNode, pVisibleOnly) {
        if (pVisibleOnly === void 0) { pVisibleOnly = false; }
        if (pNode == null)
            throw new Error(ERR_INVALID_ARGUMENT);
        var tBBox = new BoundingBox();
        tBBox.expandByNodeInPlace(pNode, pVisibleOnly, null);
        return (tBBox);
    };
    /**
     * Calculates the bounding box for the given mesh. If pVisibleOnly is true invisible nodes are ignored.
     */
    BoundingBox.FromMesh = function (pMesh, pVisibleOnly) {
        if (pVisibleOnly === void 0) { pVisibleOnly = false; }
        return (BoundingBox.FromNode(pMesh, pVisibleOnly));
    };
    /**
     * Calculates the bounding box for the given elements. If pVisibleOnly is true invisible nodes are ignored.
     */
    BoundingBox.FromElements = function (pElements, pUseWorldTransform, pVisibleOnly) {
        if (pUseWorldTransform === void 0) { pUseWorldTransform = true; }
        if (pVisibleOnly === void 0) { pVisibleOnly = false; }
        var tTotalBounds = new BoundingBox();
        for (var _i = 0, pElements_1 = pElements; _i < pElements_1.length; _i++) {
            var tElement = pElements_1[_i];
            var tBounds = pVisibleOnly ? tElement.boundingBoxVisible : tElement.boundingBox;
            tBounds = tBounds.transform(pUseWorldTransform ? tElement.worldTransform : tElement.transform);
            tTotalBounds.expandInPlace(tBounds);
        }
        return (tTotalBounds);
    };
    /**
     * Returns true if the given BabylonJS BoundingInfo is valid.
     */
    BoundingBox.IsBoundingInfoValid = function (pInfo) {
        return (BoundingBox.IsMinMaxValid(pInfo.minimum, pInfo.maximum));
    };
    //// private ///////////////////////////////////////////////////////////////////////////////
    BoundingBox.IsMinMaxValid = function (pMin, pMax) {
        return (!pMin.equalsWithEpsilon(VECTOR3_MIN, EPSILON) &&
            !pMin.equalsWithEpsilon(VECTOR3_MAX, EPSILON) &&
            !pMax.equalsWithEpsilon(VECTOR3_MIN, EPSILON) &&
            !pMax.equalsWithEpsilon(VECTOR3_MAX, EPSILON));
    };
    BoundingBox.prototype.isBoundingSphereInFrustum = function (pFrustum, pWorldMatrix) {
        if (pWorldMatrix === void 0) { pWorldMatrix = null; }
        var tCenter = this.getCenter();
        var tRadius = this.getRadius();
        if (pWorldMatrix != null) {
            Vector3.TransformCoordinatesToRef(tCenter, pWorldMatrix, tCenter);
            var tVScale = Vector3.Zero();
            Vector3.TransformNormalFromFloatsToRef(1.0, 1.0, 1.0, pWorldMatrix, tVScale);
            tRadius = Math.max(Math.abs(tVScale.x), Math.abs(tVScale.y), Math.abs(tVScale.z)) * tRadius;
        }
        for (var i = 0; i < pFrustum.length; i++) {
            if (pFrustum[i].dotCoordinate(tCenter) <= -tRadius)
                return (false);
        }
        return (true);
    };
    BoundingBox.prototype.expandByNodeInPlace = function (pNode, pVisibleOnly, pParentMatrix) {
        var tMatrix = Matrix.Identity();
        if (pParentMatrix != null) {
            getLocalMatrixToRef(pNode, tMatrix);
            tMatrix.multiplyToRef(pParentMatrix, tMatrix);
        }
        if (((pNode instanceof Mesh && pNode.geometry != null) || pNode instanceof InstancedMesh)
            && (pNode.isVisible || !pVisibleOnly)) {
            var tPositions = pNode.getVerticesData(VertexBuffer.PositionKind);
            if (tPositions != null && tPositions.length > 0) {
                var tVertex = gTmpVector3;
                for (var i = 0; i < tPositions.length; i += 3) {
                    var tX = tPositions[i];
                    var tY = tPositions[i + 1];
                    var tZ = tPositions[i + 2];
                    if (!tMatrix.isIdentity()) {
                        Vector3.TransformCoordinatesFromFloatsToRef(tX, tY, tZ, tMatrix, tVertex);
                        this.min.minimizeInPlace(tVertex);
                        this.max.maximizeInPlace(tVertex);
                    }
                    else {
                        this.min.minimizeInPlaceFromFloats(tX, tY, tZ);
                        this.max.maximizeInPlaceFromFloats(tX, tY, tZ);
                    }
                }
            }
        }
        //add children bounds
        var tChilds = pNode.getChildTransformNodes(true);
        for (var _i = 0, tChilds_1 = tChilds; _i < tChilds_1.length; _i++) {
            var tChild = tChilds_1[_i];
            this.expandByNodeInPlace(tChild, pVisibleOnly, tMatrix);
        }
    };
    /** Left side point indices. */
    BoundingBox.SIDE_MIN_X = [7, 6, 1, 0];
    /** Right side point indices. */
    BoundingBox.SIDE_MAX_X = [3, 2, 5, 4];
    /** Bottom side point indices. */
    BoundingBox.SIDE_MIN_Y = [7, 0, 3, 4];
    /** Top side point indices. */
    BoundingBox.SIDE_MAX_Y = [1, 6, 5, 2];
    /** Front side point indices. */
    BoundingBox.SIDE_MIN_Z = [0, 1, 2, 3];
    /** Back side point indices. */
    BoundingBox.SIDE_MAX_Z = [4, 5, 6, 7];
    /** Indices of the 12 box edges */
    BoundingBox.EDGES = [
        [3, 0], [0, 1], [1, 2],
        [4, 3], [3, 2], [2, 5],
        [7, 4], [4, 5], [5, 6],
        [0, 7], [7, 6], [6, 1]
    ];
    return BoundingBox;
}());
export { BoundingBox };
//// temp variables ////////////////////////////////////////////////////////////////////////////
var gTmpVector3 = new Vector3();
